import {
  BlockSuiteError,
  ErrorCode,
} from '@blocksuite/affine/global/exceptions';
import { NoopLogger } from '@blocksuite/affine/global/utils';
import {
  type Doc,
  type IdGenerator,
  nanoid,
  type Workspace,
  type WorkspaceMeta,
} from '@blocksuite/affine/store';
import {
  BlobEngine,
  type BlobSource,
  MemoryBlobSource,
} from '@blocksuite/affine/sync';
import { Subject } from 'rxjs';
import type { Awareness } from 'y-protocols/awareness.js';
import type { Doc as YDoc } from 'yjs';

import type { FeatureFlagService } from '../../feature-flag';
import { DocImpl } from './doc';
// import { WorkspaceMetaImpl } from './meta';
import { EnhancedWorkspaceMetaImpl } from './enhanced-meta';
import type { FrameworkProvider } from '@toeverything/infra';

type WorkspaceOptions = {
  id?: string;
  rootDoc: YDoc;
  blobSource?: BlobSource;
  onLoadDoc?: (doc: YDoc) => void;
  onLoadAwareness?: (awareness: Awareness) => void;
  onCreateDoc?: (docId?: string) => string;
  featureFlagService?: FeatureFlagService;
  readonly framework?: FrameworkProvider; // 新增参数
};

export class WorkspaceImpl implements Workspace {
  readonly blobSync: BlobEngine;

  readonly blockCollections = new Map<string, Doc>();

  readonly doc: YDoc;

  readonly id: string;

  readonly idGenerator: IdGenerator;

  meta: WorkspaceMeta & { dispose?: () => void };

  slots = {
    /* eslint-disable rxjs/finnish */
    docListUpdated: new Subject<void>(),
    docCreated: new Subject<string>(),
    /* eslint-enable rxjs/finnish */
  };

  // ✅ 存储订阅以便在 dispose 时取消
  private _subscriptions: { unsubscribe: () => void }[] = [];

  get docs() {
    return this.blockCollections;
  }

  readonly onLoadDoc?: (doc: YDoc) => void;
  readonly onLoadAwareness?: (awareness: Awareness) => void;
  readonly onCreateDoc?: (docId?: string) => string;
  readonly featureFlagService?: FeatureFlagService;
  private readonly framework?: FrameworkProvider;

  constructor({
    id,
    rootDoc,
    blobSource,
    onLoadDoc,
    onLoadAwareness,
    onCreateDoc,
    featureFlagService,
    framework, // 新增参数
  }: WorkspaceOptions) {
    this.id = id || '';
    this.featureFlagService = featureFlagService;
    this.framework = framework;
    this.doc = rootDoc;
    this.onLoadDoc = onLoadDoc;
    this.onLoadDoc?.(this.doc);
    this.onLoadAwareness = onLoadAwareness;
    this.onCreateDoc = onCreateDoc;

    blobSource = blobSource ?? new MemoryBlobSource();
    const logger = new NoopLogger();

    this.blobSync = new BlobEngine(blobSource, [], logger);

    this.idGenerator = nanoid;

    this.meta = new EnhancedWorkspaceMetaImpl(this.doc, framework);
    this._bindDocMetaEvents();
  }

  private _bindDocMetaEvents() {
    // ✅ 保存订阅引用以便在 dispose 时取消
    // ⚠️ 移除 eager creation: 不再监听 docMetaAdded 立即创建文档
    // 当外部调用 getDoc 时会自动懒加载
    /*
    this._subscriptions.push(
      this.meta.docMetaAdded.subscribe(docId => {
        const doc = new DocImpl({
          id: docId,
          collection: this,
          doc: this.doc,
        });
        this.blockCollections.set(doc.id, doc);
      })
    );
    */

    this._subscriptions.push(
      this.meta.docMetaUpdated.subscribe(() => this.slots.docListUpdated.next())
    );

    this._subscriptions.push(
      this.meta.docMetaRemoved.subscribe(id => {
        // 如果文档被删除，我们需要从内存中移除并 dispose
        const doc = this.blockCollections.get(id);
        if (!doc) return;
        this.blockCollections.delete(id);
        doc.remove();
      })
    );
  }

  private _hasDoc(docId: string) {
    // 检查 blockCollections 或者 meta 中是否存在
    return this.blockCollections.has(docId) || !!this.meta.getDocMeta(docId);
  }

  /**
   * By default, only an empty doc will be created.
   * If the `init` parameter is passed, a `surface`, `note`, and `paragraph` block
   * will be created in the doc simultaneously.
   */
  createDoc(docId?: string): Doc {
    if (this.onCreateDoc) {
      const id = this.onCreateDoc(docId);
      // createDoc 后需要确保实例已创建
      const doc = this.getDoc(id);
      if (!doc) {
        throw new BlockSuiteError(
          ErrorCode.DocCollectionError,
          'create doc failed'
        );
      }
      return doc;
    }
    const id = docId ?? this.idGenerator();
    // 检查是否已存在
    if (this.meta.getDocMeta(id)) {
      throw new BlockSuiteError(
        ErrorCode.DocCollectionError,
        'doc already exists'
      );
    }

    this.meta.addDocMeta({
      id,
      title: '',
      createDate: Date.now(),
      tags: [],
    });
    return this.getDoc(id) as Doc;
  }

  private _getDoc(docId: string): Doc | null {
    // 1. 尝试从内存缓存获取
    if (this.blockCollections.has(docId)) {
      return this.blockCollections.get(docId)!;
    }

    // 2. 尝试懒加载：如果元数据存在，则创建实例
    if (this.meta.getDocMeta(docId)) {
      const doc = new DocImpl({
        id: docId,
        collection: this,
        doc: this.doc,
      });
      this.blockCollections.set(docId, doc);
      this.slots.docCreated.next(docId);
      return doc;
    }

    return null;
  }

  getDoc(docId: string): Doc | null {
    const doc = this._getDoc(docId);
    return doc;
  }

  /**
   * ✅ 主动驱逐文档（释放内存）
   * 当 DocsService 的 ObjectPool 释放文档时调用
   */
  evictDoc(docId: string) {
    const doc = this.blockCollections.get(docId);
    if (doc) {
      // 调用 dispose 释放资源 (Awareness, YDoc binding 等)
      // 注意：DocImpl.dispose() 通常不销毁持久化数据，只销毁内存对象
      doc.dispose();
      this.blockCollections.delete(docId);
    }
  }

  removeDoc(docId: string) {
    const docMeta = this.meta.getDocMeta(docId);
    if (!docMeta) {
      throw new BlockSuiteError(
        ErrorCode.DocCollectionError,
        `doc meta not found: ${docId}`
      );
    }

    const blockCollection = this._getDoc(docId);
    if (!blockCollection) return;

    blockCollection.dispose();
    this.meta.removeDocMeta(docId);
    this.blockCollections.delete(docId);
  }

  dispose() {
    // ✅ 取消所有订阅
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._subscriptions = [];

    // ✅ 销毁所有 blockCollections
    this.blockCollections.forEach(doc => doc.dispose());
    this.blockCollections.clear();

    // ✅ 清理 meta（取消 Yjs 观察者和完成 Subjects）
    this.meta.dispose?.();

    // ✅ 销毁 rootDoc 的 Awareness（会触发 clearInterval）
    // Awareness 在 doc.on('destroy') 时会自动调用 destroy()
    // 所以我们需要先销毁 rootDoc
    if (this.doc) {
      this.doc.destroy();
    }

    // ✅ 清理 slots
    this.slots.docListUpdated.complete();
  }
}
